import { useEffect, useMemo, useState } from 'react';
import { useOnlineStatus } from 'hooks/useOnlineStatus';
import type { StorageUnit } from 'store/storageunits/types';
import { StorageUnitType } from 'store/storageunits/types';
import { hasOfflineFileData } from 'store/storageunits/utils';
import { storageUnitCache } from 'util/cache/storageUnitCacheIndexedDB';

const hasPendingOfflineFileChanges = (unit: StorageUnit) =>
  unit.pendingAction === 'create' ||
  unit.pendingAction === 'update' ||
  unit.pendingAction === 'archive';

export const useSelectableFileUnits = (units: StorageUnit[]) => {
  const isOnline = useOnlineStatus();
  const fileUnits = useMemo(
    () => units.filter((unit) => unit.type === StorageUnitType.File),
    [units],
  );
  const [offlineSelectableFileIds, setOfflineSelectableFileIds] = useState<
    Set<string>
  >(new Set());
  const [offlineSelectionReady, setOfflineSelectionReady] = useState(true);

  useEffect(() => {
    let cancelled = false;

    if (isOnline) {
      setOfflineSelectableFileIds(new Set());
      setOfflineSelectionReady(true);
      return () => {
        cancelled = true;
      };
    }

    setOfflineSelectionReady(false);
    void Promise.all(
      fileUnits.map(async (unit) => {
        if (hasPendingOfflineFileChanges(unit) || hasOfflineFileData(unit)) {
          return [unit.id, true] as const;
        }

        const cached = await storageUnitCache.getOne(unit.id).catch(() => null);
        return [unit.id, hasOfflineFileData(cached)] as const;
      }),
    )
      .then((results) => {
        if (cancelled) {
          return;
        }

        setOfflineSelectableFileIds(
          new Set(
            results
              .filter(([, isSelectable]) => isSelectable)
              .map(([id]) => id),
          ),
        );
        setOfflineSelectionReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setOfflineSelectionReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fileUnits, isOnline]);

  const selectableFileUnits = useMemo(() => {
    if (isOnline) {
      return fileUnits;
    }
    if (!offlineSelectionReady) {
      return [];
    }
    return fileUnits.filter((unit) => offlineSelectableFileIds.has(unit.id));
  }, [fileUnits, isOnline, offlineSelectableFileIds, offlineSelectionReady]);

  const selectableFileIds = useMemo(
    () => new Set(selectableFileUnits.map((unit) => unit.id)),
    [selectableFileUnits],
  );

  return {
    isOnline,
    selectableFileIds,
    selectableFileUnits,
    selectionReady: offlineSelectionReady,
  };
};
